import {
  faCircleInfo,
  faCloudArrowUp,
  faCopy,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useId, useRef } from 'react';
import { toast } from 'react-toastify';

import MainButton from '../../components/agv-button';
import DragAndDrop from '../../components/agv-drag-and-drop';

// Primitives visuelles de l'écran, reprises de la maquette Figma « fleet-next »
// (cartes à bord #e6e8ec, libellés de bloc en capitales 11 px).

export const Card = ({ title, subtitle, actions, children }) => (
  <div className="tw-flex tw-flex-col tw-rounded-[10px] tw-border tw-border-solid tw-border-[#e6e8ec] tw-bg-white">
    {(title || actions) && (
      <div className="tw-flex tw-items-start tw-gap-3 tw-pb-3.5 tw-pl-6 tw-pr-5 tw-pt-[18px]">
        <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
          <p className="tw-m-0 tw-text-[16px] tw-font-semibold tw-text-text-dark-high-importance">
            {title}
          </p>
          {subtitle && (
            <p className="tw-m-0 tw-text-[12px] tw-text-[#76808c]">
              {subtitle}
            </p>
          )}
        </div>
        {actions}
      </div>
    )}
    <div className="tw-flex tw-flex-col tw-gap-4 tw-px-6 tw-pb-6">
      {children}
    </div>
  </div>
);

/**
 * Bloc de formulaire : titre et aide à gauche, champs à droite (empilés sur
 * petit écran). Sert à découper chaque section de la fiche client.
 */
export const FormSection = ({ title, description, actions, children }) => (
  <div className="tw-grid tw-grid-cols-[220px_minmax(0,1fr)] tw-gap-6 tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] tw-py-5 first:tw-pt-0 last:tw-border-b-0 last:tw-pb-0 mq750:tw-grid-cols-1 mq750:tw-gap-3">
    <div className="tw-flex tw-flex-col tw-gap-1">
      <p className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-text-dark-high-importance">
        {title}
      </p>
      {description && (
        <p className="tw-m-0 tw-text-[12px] tw-leading-[17px] tw-text-[#76808c]">
          {description}
        </p>
      )}
      {actions && (
        <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2">{actions}</div>
      )}
    </div>
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">{children}</div>
  </div>
);

/**
 * Panneau blanc qui contient les blocs d'une section.
 */
export const Panel = ({ children }) => (
  <div className="tw-rounded-[10px] tw-border tw-border-solid tw-border-[#e6e8ec] tw-bg-white tw-px-6 tw-py-5">
    {children}
  </div>
);

export const copyToClipboard = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copié.`);
  } catch {
    toast.error('Copie impossible : sélectionnez le texte et copiez-le.');
  }
};

/**
 * Value to copy (sign-up link, API key...): read-only field
 * that selects itself on focus, and a "Copier" button.
 */
export const CopyField = ({ label, value }) => (
  <div className="tw-flex tw-flex-col tw-gap-1">
    <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-[rgba(0,0,0,0.45)]">
      {label}
    </span>
    <div className="tw-flex tw-items-center tw-gap-2">
      <input
        readOnly
        aria-label={label}
        value={value}
        onFocus={(event) => event.target.select()}
        className="tw-h-8 tw-min-w-0 tw-flex-1 tw-rounded-[5px] tw-border tw-border-solid tw-border-input-border tw-bg-[rgba(118,128,140,0.04)] tw-px-2.5 tw-font-mono tw-text-[12px]"
      />
      <MainButton
        variant="secondary"
        icon={faCopy}
        text="Copier"
        onClick={() => copyToClipboard(value, label)}
      />
    </div>
  </div>
);

export const Toggle = ({ checked, onChange, label, description }) => {
  const inputId = useId();

  return (
    <div className="tw-flex tw-items-start tw-gap-2">
      <input
        id={inputId}
        aria-describedby={`${inputId}-description`}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="tw-mt-0.5 tw-size-4 tw-cursor-pointer tw-accent-main"
      />
      <span className="tw-flex tw-flex-col">
        <label
          htmlFor={inputId}
          className="tw-cursor-pointer tw-text-[13px] tw-text-black"
        >
          {label}
        </label>
        <span
          id={`${inputId}-description`}
          className="tw-text-[11px] tw-text-[#76808c]"
        >
          {description}
        </span>
      </span>
    </div>
  );
};

export const SectionLabel = ({ children, hint }) => (
  <p className="tw-m-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-text-[rgba(0,0,0,0.45)]">
    {children}
    {hint && (
      <span className="tw-font-normal tw-normal-case">
        {'  ·  '}
        {hint}
      </span>
    )}
  </p>
);

const BANNER_STYLES = {
  info: {
    icon: faCircleInfo,
    container: 'tw-border-[rgba(7,153,146,0.2)] tw-bg-[rgba(7,153,146,0.06)]',
    iconColor: 'tw-text-main',
    text: 'tw-text-main-active',
  },
  warn: {
    icon: faTriangleExclamation,
    container: 'tw-border-amber-200 tw-bg-amber-50',
    iconColor: 'tw-text-amber-500',
    text: 'tw-text-amber-800',
  },
};

export const Banner = ({ tone = 'info', children }) => {
  const style = BANNER_STYLES[tone];

  return (
    <div
      className={`tw-flex tw-gap-3 tw-rounded-[6px] tw-border tw-border-solid tw-p-3 ${style.container}`}
    >
      <FontAwesomeIcon
        icon={style.icon}
        className={`tw-mt-0.5 tw-size-3.5 tw-shrink-0 ${style.iconColor}`}
      />
      <div className={`tw-text-[12px] ${style.text}`}>{children}</div>
    </div>
  );
};

const getExtension = (fileName) => {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex === -1 ? '' : fileName.slice(dotIndex).toLowerCase();
};

/**
 * Zone de dépôt : glisser-déposer ou clic pour parcourir. Le format et la
 * taille sont vérifiés ici pour éviter un aller-retour inutile vers l'API,
 * qui refait les mêmes contrôles.
 */
export const FileDropZone = ({
  accept,
  maxSizeMb,
  onFile,
  disabled,
  children,
}) => {
  const inputRef = useRef(null);

  const handleFiles = (files) => {
    const file = files?.[0];
    if (!file || disabled) return;

    if (!accept.includes(getExtension(file.name))) {
      toast.error(
        `Format non autorisé. Formats acceptés : ${accept.join(', ')}`
      );
      return;
    }

    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`Le fichier dépasse ${maxSizeMb} Mo.`);
      return;
    }

    onFile(file);
  };

  // DragAndDrop n'enregistre son écouteur qu'au montage : on passe par une
  // ref pour qu'un dépôt utilise toujours les props courantes.
  const handleFilesRef = useRef(handleFiles);
  useEffect(() => {
    handleFilesRef.current = handleFiles;
  });

  return (
    <DragAndDrop handleDrop={(files) => handleFilesRef.current(files)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-[6px] tw-border tw-border-dashed tw-border-[#d0d4da] tw-bg-[rgba(118,128,140,0.04)] tw-px-4 tw-py-6 tw-text-center hover:tw-border-main disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
      >
        {children || (
          <>
            <FontAwesomeIcon
              icon={faCloudArrowUp}
              className="tw-size-5 tw-text-main"
            />
            <span className="tw-text-[13px] tw-text-black">
              Glissez un fichier ici ou{' '}
              <span className="tw-font-medium tw-text-main">parcourez</span>
            </span>
          </>
        )}
        <span className="tw-text-[11px] tw-text-[#76808c]">
          {accept.join(', ')} · {maxSizeMb} Mo max.
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept.join(',')}
        className="tw-hidden"
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </DragAndDrop>
  );
};

export const formatFileSize = (size) => {
  if (!size && size !== 0) return '—';
  if (size < 1024) return `${size} o`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} Ko`;
  return `${(size / 1024 / 1024).toFixed(1)} Mo`;
};
